import Hls from 'hls.js';

export default class MediaControl {
  /// Holds one single IntersectionObserver for all videos on the page
  static observer;

  /// Set's the minimum threshold for videos that need play/pause controls for accessibility
  static a11yThreshold = 5;

  /// Reduced motion media query object
  static reducedMotionMQ = window.matchMedia('(prefers-reduced-motion: reduce)');

  /// The status of the device's low power mode setting
  static automaticPlaybackDisabled;

  /// Class selector constants
  static selectors = {
    buttonClass: 'c-media-control',
    labelClass: 'c-media-control__label',
    hiddenClass: 'c-media-control--hidden',
    fadedClass: 'c-media-control--faded',
    requiredClass: 'c-media-control--required',
  };

  /// HLS manifest mime types.
  static hlsMimeTypes = [
    'application/vnd.apple.mpegurl',
    'application/x-mpegurl',
  ];

  /// Alias for MediaControl.reducedMotionMQ.matches
  static get reducedMotion() {
    return MediaControl.reducedMotionMQ.matches;
  }

  /**
   * Creates the global IntersectionObserver instance for all videos and calls
   * createInstances when the page is ready.
   */
  static async init() {
    if (MediaControl.automaticPlaybackDisabled === undefined) {
      MediaControl.automaticPlaybackDisabled = await MediaControl.testAutomaticPlayback();
    }

    MediaControl.observer = new IntersectionObserver(
      MediaControl.intersectionCallback,
      { threshold: 0 },
    );

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', MediaControl.createInstances);
    } else {
      MediaControl.createInstances();
    }
  }

  /**
   * Checks for the ability to autoplay videos without user interaction
   * @returns {Promise}
   */
  static async testAutomaticPlayback() {
    const testVideo = document.createElement('video');
    const source = document.createElement('source');

    testVideo.playsInline = true;
    testVideo.muted = true;
    testVideo.style.visibility = 'hidden';
    source.src = `${window.themeDirectoryUri}/resources/videos/blank.mp4`;
    source.type = 'video/mp4';

    testVideo.appendChild(source);
    document.documentElement.appendChild(testVideo);

    try {
      await testVideo.play();
      return false;
    } catch (error) {
      console.info('Autoplay disabled', error.message);
      return true;
    } finally {
      testVideo.remove();
    }
  }

  /**
   * Callback for IntersectionObserver that handles play/pause logic while the
   * user scrolls.
   * @param {Array<IntersectionObserverEntry>} entries
   */
  static intersectionCallback(entries) {
    entries.forEach(async (entry) => {
      const video = entry.target.observedVideo;
      const { mediaControl } = video;

      if (!mediaControl) {
        return;
      }

      mediaControl.isIntersecting = entry.isIntersecting;

      const {
        automaticPlaybackDisabled,
        autoplay,
        canPlay,
        isIntersecting,
        userPaused,
        userPlay,
      } = mediaControl;

      const { ended, paused } = video;

      const { reducedMotion } = MediaControl;

      // Paused by the system and not user input
      const systemPaused = paused && !userPaused && !ended;

      // Autoplay is enabled and should be honored
      const shouldAutoplay = autoplay && !reducedMotion;

      // Either autoplay or resume user-initiated play
      const shouldPlay = shouldAutoplay || userPlay;

      if (automaticPlaybackDisabled) {
        return;
      }

      if (!isIntersecting && !paused) {
        video.pause();
      } else if (
        isIntersecting
        && systemPaused
        && canPlay
        && shouldPlay
      ) {
        try {
          await video.play();
        } catch (error) {
          console.warn('Playback failed', error.message);
        }
      }
    });
  }

  /**
   * Factory method for creating and instance of MediaControl and adding it
   * to the static IntersectionObserver instance.
   */
  static createInstances() {
    document.querySelectorAll('video').forEach(MediaControl.setupHls);

    const buttons = document.querySelectorAll(`.${MediaControl.selectors.buttonClass}`);
    buttons.forEach((button) => {
      const videoId = button.dataset.videoId.replaceAll('/', '\\/');

      if (!videoId) {
        return;
      }

      const videos = document.querySelectorAll(`video#${videoId}, #${videoId} video`);

      if (videos.length === 1) {
        const instance = new MediaControl(button, videos[0]);
        MediaControl.observer.observe(instance.observerTarget);
      } else if (videos.length > 1) {
        const videoLoadPromises = [...videos].map((video) => new Promise((resolve) => {
          if (video.readyState < HTMLMediaElement.HAVE_METADATA) {
            video.addEventListener('loadedmetadata', () => {
              resolve();
            });
          } else {
            resolve();
          }
        }));

        Promise.all(videoLoadPromises).then(() => {
          const sorted = [...videos].sort((a, b) => b.duration - a.duration);

          const instance = new MediaControl(button, sorted[0], sorted.slice(1));
          MediaControl.observer.observe(instance.observerTarget);
        });
      }
    });
  }

  /**
   * Guesses the mime type of a video from the filename extension.
   * @param {String} filename
   */
  static guessMimeFromFilename(filename) {
    const ext = MediaControl.getUrlPathname(filename).split('.').pop().toLowerCase();

    switch (ext) {
      case 'mp4':
        return 'video/mp4';
      case 'mov':
        return 'video/quicktime';
      case 'webm':
        return 'video/webm';
      case 'm3u8':
        return 'application/vnd.apple.mpegurl';
      default:
        return false;
    }
  }

  /**
   * Returns the path portion of a URL, preserving support for relative paths.
   * @param {String} url
   */
  static getUrlPathname(url) {
    try {
      return new URL(url, window.location.href).pathname;
    } catch (error) {
      return url.split('?')[0];
    }
  }

  /**
   * Checks whether a source element or URL points to an HLS manifest.
   * @param {String} src
   * @param {String} type
   */
  static isHlsSource(src = '', type = '') {
    return MediaControl.hlsMimeTypes.includes(type.toLowerCase())
      || MediaControl.getUrlPathname(src).toLowerCase().endsWith('.m3u8');
  }

  /**
   * Attaches hls.js for browsers without native HLS support.
   * @param {HTMLVideoElement} video
   */
  static setupHls(video) {
    const sources = [...video.querySelectorAll(':scope > source')];
    const source = sources.find((item) => MediaControl.isHlsSource(item.src, item.type));

    if (!source || video.canPlayType('application/vnd.apple.mpegurl') || video.hls) {
      return;
    }

    if (!Hls.isSupported()) {
      return;
    }

    const hls = new Hls();
    hls.loadSource(source.src);
    hls.attachMedia(video);
    /* eslint-disable-next-line no-param-reassign */
    video.hls = hls;
  }

  /// Updates the media button's required class when requiredControls is updated
  set requiredControls(value) {
    if (value) {
      this.button.classList.add(MediaControl.selectors.requiredClass);
    } else {
      this.button.classList.remove(MediaControl.selectors.requiredClass);
    }
  }

  /**
   * Constructs a MediaControl instance given a button and its associated video
   * @param {HTMLButtonElement} button
   * @param {HTMLVideoElement} video
   */
  constructor(button, video, puppets = []) {
    this.button = button;
    this.video = video;
    this.puppets = puppets;
    this.allVideos = [this.video, ...this.puppets];
    this.allVideos.forEach(MediaControl.setupHls);
    this.label = this.button.querySelector(`.${MediaControl.selectors.labelClass}`);
    this.observerTarget = document.getElementById(this.button.dataset.observerTarget) ?? this.video;
    this.observerTarget.observedVideo = this.video;
    this.automaticPlaybackDisabled = MediaControl.automaticPlaybackDisabled;

    this.loop = this.video.loop;
    this.autoplay = this.video.autoplay;
    this.canPlay = this.videoCanPlay();
    this.requiredControls = false;
    this.userPaused = false;
    this.userPlay = false;

    if (!this.video.id) {
      this.video.id = crypto.randomUUID();
    }

    this.button.setAttribute('aria-controls', this.video.id);

    if (this.loop) {
      this.requiredControls = true;
    }

    /* eslint-disable no-param-reassign */
    this.allVideos.forEach((v) => {
      v.removeAttribute('autoplay');

      if (this.autoplay && !v.hls && v.readyState === HTMLMediaElement.HAVE_NOTHING) {
        v.load();
      }
    });
    /* eslint-enable no-param-reassign */

    if (this.automaticPlaybackDisabled) {
      this.button.classList.remove(MediaControl.selectors.hiddenClass);
    }

    this.registerEventListeners();

    this.button.mediaControl = this;
    this.allVideos.forEach((v) => {
      /* eslint-disable-next-line no-param-reassign */
      v.mediaControl = this;
    });
  }

  /**
   * Pauses the video
   */
  pause = () => {
    if (!this.video.paused) {
      this.video.pause();
    }
  };

  /**
   * Play's the video only if the following criteria is met:
   * - Video is paused
   * - Video is NOT ended
   * - Video source can play
   * - Video was NOT paused by the user
   *
   * And EITHER of these are true:
   * - Video is set to autoplay and reduced motion is NOT enabled
   * - Video playback was initiated by the user
   */
  playIfAble = async () => {
    const { reducedMotion } = MediaControl;

    const {
      ended,
      paused,
    } = this.video;

    const {
      automaticPlaybackDisabled,
      autoplay,
      canPlay,
      userPaused,
      userPlay,
    } = this;

    if (
      !automaticPlaybackDisabled
      && paused
      && !ended
      && canPlay
      && !userPaused
      && (
        (
          autoplay
          && !reducedMotion
        ) || userPlay
      )
    ) {
      try {
        await this.video.play();
      } catch (error) {
        console.warn('Playback failed', error.message);
      }
    }
  };

  /**
   * Updates traits that rely on the video's metadata being available.
   */
  afterVideoMetaLoaded = () => {
    if (this.video.duration > MediaControl.a11yThreshold) {
      this.requiredControls = true;
      this.button.classList.remove(MediaControl.selectors.hiddenClass);
    }
  };

  /**
   * Registers video, button and media query listeners.
   */
  registerEventListeners() {
    if (this.canPlay) {
      if (this.loop || !this.autoplay || MediaControl.reducedMotion) {
        this.button.classList.remove(MediaControl.selectors.hiddenClass);
      }

      if (this.video.readyState < HTMLMediaElement.HAVE_METADATA) {
        this.video.addEventListener('loadedmetadata', this.afterVideoMetaLoaded);
      } else {
        this.afterVideoMetaLoaded();
      }
    }

    this.video.addEventListener('ended', () => {
      this.userPaused = false;
      this.userPlay = false;

      if (this.loop) {
        return;
      }

      this.toggleState('play');
      this.button.classList.remove(MediaControl.selectors.hiddenClass);
      this.button.classList.remove(MediaControl.selectors.fadedClass);
    });

    this.video.addEventListener('play', () => {
      /* eslint-disable no-param-reassign */
      if (this.automaticPlaybackDisabled) {
        this.allVideos.forEach((v) => {
          const picture = v.nextElementSibling;
          if (picture && picture.tagName === 'PICTURE') {
            picture.remove();
          }
          v.style.display = '';
        });
      }
      /* eslint-enable no-param-reassign */

      this.toggleState('pause');
      this.userPaused = false;
      if (this.video.duration > MediaControl.a11yThreshold || this.loop) {
        this.button.classList.remove(MediaControl.selectors.hiddenClass);
        this.button.classList.remove(MediaControl.selectors.fadedClass);
      }

      this.puppets.forEach(async (puppet) => {
        if (!puppet.ended) {
          try {
            await puppet.play();
          } catch (error) {
            console.warn('Playback failed', error.message);
          }
        }
      });
    });

    if (this.loop) {
      this.allVideos.forEach((video) => {
        if (MediaControl.isHlsSource(video.currentSrc)) {
          // Restart HLS playback by seeking, not reloading. video.load() detaches the
          // MediaSource buffer and re-exposes the poster, causing a visible flash each loop.
          video.addEventListener('timeupdate', () => {
            if (video.currentTime >= video.duration - 0.2) {
              /* eslint-disable-next-line no-param-reassign */
              video.currentTime = 0;
              if (video.paused) {
                video.play().catch(() => {});
              }
            }
          });
        }
      });
    }

    this.video.addEventListener('pause', () => {
      if (!this.video.ended) {
        this.toggleState('play');
      }

      this.puppets.forEach((puppet) => {
        puppet.pause();
      });
    });

    function preventBubbling(event) {
      event.stopPropagation();
    }

    this.button.addEventListener('pointerdown', preventBubbling);
    this.button.addEventListener('pointermove', preventBubbling);
    this.button.addEventListener('pointercancel', preventBubbling);
    this.button.addEventListener('pointerup', preventBubbling);

    this.button.addEventListener('click', async (event) => {
      event.stopPropagation();

      if (this.video.paused || this.video.ended) {
        if (this.canPlay) {
          try {
            await this.video.play();
            MediaControl.automaticPlaybackDisabled = false;
            this.automaticPlaybackDisabled = false;
            this.userPlay = true;
          } catch (error) {
            console.warn('Playback failed', error.message);
          }
        }
      } else {
        this.video.pause();
        this.userPaused = true;
      }
    });

    MediaControl.reducedMotionMQ.addEventListener('change', async (event) => {
      if (event.matches) {
        this.button.classList.remove(MediaControl.selectors.hiddenClass);
        this.button.classList.remove(MediaControl.selectors.fadedClass);
        this.userPlay = false;
        this.userPause = false;
        this.toggleState('play');
        this.allVideos.forEach((video) => {
          video.load();
        });
      } else if (
        this.canPlay
        && this.isIntersecting
      ) {
        if (this.autoplay) {
          try {
            await this.video.play();
          } catch (error) {
            console.warn('Playback failed', error.message);
          }
        } else {
          this.button.classList.remove(MediaControl.selectors.hiddenClass);
          this.button.classList.remove(MediaControl.selectors.fadedClass);
          this.allVideos.forEach((video) => {
            video.load();
          });
        }
      }
    });
  }

  /**
   * Toggles the button state and sets the label text
   * @param {String} state - The state to show, either `play` or `pause`
   */
  toggleState(state) {
    switch (state) {
      case 'play':
        this.button.ariaPressed = false;
        if (this.label) {
          this.label.innerHTML = this.button.dataset.labelPlay;
        }
        break;
      case 'pause':
        this.button.ariaPressed = true;
        if (this.label) {
          this.label.innerHTML = this.button.dataset.labelPause;
        }
        break;
      default:
        console.warn('`toggleState` only accepts "play" and "pause" as values');
        break;
    }
  }

  /**
   * Checks whether at least one source element can by played by the browser. If
   * no source elements are present, tries to approximate using video.src.
   * @param {HTMLVideoElement} video
   */
  videoCanPlay() {
    const sources = this.video.querySelectorAll(':scope > source');
    if (sources.length) {
      return [...sources]
        .some((source) => {
          if (MediaControl.isHlsSource(source.src, source.type)) {
            return this.video.canPlayType('application/vnd.apple.mpegurl') !== '' || Hls.isSupported();
          }

          if (source.type) {
            return this.video.canPlayType(source.type) !== '';
          }

          if (source.src) {
            const mime = MediaControl.guessMimeFromFilename(source.src);
            if (mime) {
              return this.video.canPlayType(mime) !== '';
            }
          }

          return false;
        });
    }

    if (this.video.src) {
      if (MediaControl.isHlsSource(this.video.src)) {
        return this.video.canPlayType('application/vnd.apple.mpegurl') !== '' || Hls.isSupported();
      }

      const mime = MediaControl.guessMimeFromFilename(this.video.src);
      if (mime) {
        return this.video.canPlayType(mime) !== '';
      }
    }

    return true;
  }
}
